import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { PageHero } from "./Universita";
import { UNIVERSITIES, ALL_COURSES_FLAT } from "../data/universities";
import { getCourseImage } from "../data/courseImages";

const LEVELS = ["Tutti", "Triennale", "Magistrale", "Master"];

export default function CorsiLaurea() {
  const [level, setLevel] = useState("Tutti");
  const [uni, setUni] = useState("Tutti");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return ALL_COURSES_FLAT.filter((c) => {
      if (level !== "Tutti" && c.level !== level) return false;
      if (uni !== "Tutti" && c.uniId !== uni) return false;
      if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [level, uni, q]);

  return (
    <div data-testid="page-corsi">
      <PageHero
        eyebrow="Offerta formativa"
        title="Corsi di Laurea & Master."
        sub="Lauree triennali, magistrali, cicli unici a 5 anni e master di I° e II° livello — confronta e filtra l'offerta dei tre atenei."
      />

      <section className="py-12 lg:py-16 bg-white border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca un corso (es. Psicologia, Economia, Ingegneria)"
                className="w-full h-12 pl-11 pr-4 rounded-full border border-brand-line bg-brand-surface text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-navy"
                data-testid="course-search"
              />
            </div>

            <div className="flex gap-2 flex-wrap" data-testid="level-filters">
              {LEVELS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLevel(l)}
                  className={`px-4 h-12 rounded-full text-sm font-semibold transition-all ${
                    level === l ? "bg-brand-navy text-white" : "bg-brand-surface text-brand-ink hover:bg-brand-surfaceAlt"
                  }`}
                  data-testid={`filter-level-${l}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap" data-testid="uni-filters">
              <button
                onClick={() => setUni("Tutti")}
                className={`px-4 h-12 rounded-full text-sm font-semibold border ${
                  uni === "Tutti" ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-brand-line text-brand-ink"
                }`}
                data-testid="filter-uni-all"
              >
                Tutti gli atenei
              </button>
              {UNIVERSITIES.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setUni(u.id)}
                  className={`px-4 h-12 rounded-full text-sm font-semibold border ${
                    uni === u.id ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-brand-line text-brand-ink"
                  }`}
                  data-testid={`filter-uni-${u.id}`}
                >
                  {u.short}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mb-6 text-sm text-brand-muted">
            <span className="font-bold text-brand-navy">{filtered.length}</span> corsi disponibili
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-grid">
            {filtered.map((c) => (
              <div key={`${c.uniId}-${c.level}-${c.name}`} className="lift-card group rounded-2xl bg-white border border-brand-line overflow-hidden flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={getCourseImage(c.uniId, c.name)}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/15 to-transparent" />
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full backdrop-blur ${
                      c.level === "Master" ? "bg-amber-100/95 text-amber-900" : c.level === "Magistrale" ? "bg-violet-100/95 text-violet-900" : "bg-emerald-100/95 text-emerald-900"
                    }`}>{c.level}</span>
                    <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-white/95 text-brand-navy">
                      {c.uni}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold text-lg text-brand-navy leading-tight">{c.name}</h3>
                  <div className="mt-auto pt-5 flex items-center justify-between text-sm">
                    <span className="text-brand-muted">Da <span className="font-bold text-brand-navy">125€/mese</span></span>
                    <Link to="/contatti" state={{ uni: c.uniId, corso: c.name }} className="text-brand-emerald font-bold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`course-cta-${c.uniId}`}>
                      Info <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-brand-muted" data-testid="no-results">
              Nessun corso corrisponde ai filtri. Prova a modificare la ricerca.
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-heading font-extrabold text-2xl sm:text-3xl">Non sai quale corso scegliere?</h3>
          <p className="mt-3 text-white/80">Orientamento gratuito con un tutor del Polo in 48h.</p>
          <Link to="/orientamento" className="btn-primary mt-6 inline-flex" data-testid="corsi-orientamento-cta">
            Richiedi Orientamento <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
